import { isBotDifficulty, type BotDifficulty } from "../../../bot/difficulty";
import {
  isPlayer,
  parseGame,
  parseLegacyGame,
  parseVersion2Game,
  parseVersion3Game,
  serializeGame,
  serializeLegacyGame,
  serializeVersion2Game,
  serializeVersion3Game,
  type GameOutcome,
  type GameState,
  type Player,
  type SavedGameV1,
} from "../../../game";
import { isMatchMode, type MatchMode } from "../matchMode";
import {
  createMatchRecording,
  CLEANUP_PASSAGE_MATCH_RULES_VERSION,
  LEGACY_MATCH_RULES_VERSION,
  PRE_REPETITION_MATCH_RULES_VERSION,
  parseMatchRecording,
  recordingEndsInThreefoldRepetition,
  recordingMatchesGame,
  usesRepetitionRules,
  type ManualDrawResult,
  type MatchRecordingV1,
  type RepetitionDrawResult,
} from "./matchRecord";

export const SAVED_MATCH_SCHEMA_VERSION = 3 as const;
export const BOT_DIFFICULTY_POLICY_VERSION = 1 as const;
export const BOT_SEED_STRATEGY = "epoch-revision-fnv1a-32-v1" as const;
export const MAX_MATCH_EPOCH_LENGTH = 128;
/** Live saves reserve one revision so a tombstone can always install a successor CAS token. */
export const MAX_SAVED_MATCH_REVISION = Number.MAX_SAFE_INTEGER - 1;
export const MAX_MATCH_VERSION_REVISION = Number.MAX_SAFE_INTEGER;

export type MatchVersion = Readonly<{
  epoch: string;
  revision: number;
}>;

export type MatchResult = GameOutcome | ManualDrawResult | RepetitionDrawResult;

export type SavedMatchController =
  | Readonly<{
      route: "active";
      result: null;
    }>
  | Readonly<{
      route: "finished";
      result: MatchResult;
    }>;

export type SavedBotPolicyV1 = Readonly<{
  difficulty: BotDifficulty;
  difficultyPolicyVersion: typeof BOT_DIFFICULTY_POLICY_VERSION;
  seedStrategy: typeof BOT_SEED_STRATEGY;
}>;

export type SavedMatchV3 = Readonly<{
  schemaVersion: typeof SAVED_MATCH_SCHEMA_VERSION;
  epoch: string;
  revision: number;
  game: SavedGameV1;
  mode: MatchMode;
  humanPlayer: Player;
  bot: SavedBotPolicyV1;
  controller: SavedMatchController;
  /** Null only for matches restored from a pre-history save. */
  recording: MatchRecordingV1 | null;
}>;

/** Source-compatible aliases retained for callers that consume the current save contract. */
export type SavedMatchV2 = SavedMatchV3;
export type SavedMatchV1 = SavedMatchV3;

export type SavedMatchParseErrorCode =
  | "malformed"
  | "unsupported-version"
  | "unsupported-bot-policy"
  | "invalid-game"
  | "inconsistent-controller";

export type SavedMatchParseError = Readonly<{
  code: SavedMatchParseErrorCode;
  message: string;
  details?: readonly string[];
}>;

export type SavedMatchParseResult =
  | Readonly<{ ok: true; match: SavedMatchV3 }>
  | Readonly<{ ok: false; error: SavedMatchParseError }>;

export type CreateSavedMatchInput = Readonly<{
  epoch: string;
  revision: number;
  game: GameState;
  mode?: MatchMode;
  humanPlayer: Player;
  difficulty: BotDifficulty;
  /** Leave null for an active or engine-finished match; history-based draws are controller-owned. */
  controllerResult?: MatchResult | null;
  recording?: MatchRecordingV1 | null;
}>;

export function isMatchVersion(value: unknown): value is MatchVersion {
  if (!isRecord(value) || !hasExactlyKeys(value, ["epoch", "revision"])) return false;
  return isMatchEpoch(value["epoch"]) && isMatchVersionRevision(value["revision"]);
}

export function versionOf(match: SavedMatchV3): MatchVersion {
  return Object.freeze({ epoch: match.epoch, revision: match.revision });
}

export function sameMatchVersion(left: MatchVersion, right: MatchVersion): boolean {
  return left.epoch === right.epoch && left.revision === right.revision;
}

export function createSavedMatch(input: CreateSavedMatchInput): SavedMatchV3 {
  const game = serializeGameForRecording(input.game, input.recording ?? null);
  const result = input.controllerResult ?? input.game.outcome;
  const controller: SavedMatchController =
    result === null
      ? Object.freeze({ route: "active", result: null })
      : Object.freeze({ route: "finished", result: freezeMatchResult(result) });

  const candidate: SavedMatchV3 = Object.freeze({
    schemaVersion: SAVED_MATCH_SCHEMA_VERSION,
    epoch: input.epoch,
    revision: input.revision,
    game,
    mode: input.mode ?? "bot",
    humanPlayer: input.humanPlayer,
    bot: Object.freeze({
      difficulty: input.difficulty,
      difficultyPolicyVersion: BOT_DIFFICULTY_POLICY_VERSION,
      seedStrategy: BOT_SEED_STRATEGY,
    }),
    controller,
    recording: input.recording ?? null,
  });

  const parsed = parseSavedMatch(candidate);
  if (!parsed.ok) {
    throw new TypeError(parsed.error.message);
  }
  return parsed.match;
}

/**
 * Parses the current persisted envelope. There are deliberately no implicit
 * migrations: adding a version requires an explicit branch and fixture here.
 */
export function parseSavedMatch(input: unknown): SavedMatchParseResult {
  if (!isRecord(input)) return invalid("malformed", "Saved match must be an object.");
  if (!("schemaVersion" in input)) {
    return invalid("malformed", "Saved match schema version is missing.");
  }
  const legacyV1 = input["schemaVersion"] === 1;
  const legacyV2 = input["schemaVersion"] === 2;
  if (!legacyV1 && !legacyV2 && input["schemaVersion"] !== SAVED_MATCH_SCHEMA_VERSION) {
    return invalid("unsupported-version", "Saved match schema version is not supported.");
  }
  const keys = legacyV1
    ? ["schemaVersion", "epoch", "revision", "game", "humanPlayer", "bot", "controller"]
    : legacyV2
      ? ["schemaVersion", "epoch", "revision", "game", "mode", "humanPlayer", "bot", "controller"]
      : [
          "schemaVersion",
          "epoch",
          "revision",
          "game",
          "mode",
          "humanPlayer",
          "bot",
          "controller",
          "recording",
        ];
  if (
    !hasExactlyKeys(input, keys) ||
    !isMatchEpoch(input["epoch"]) ||
    !isSavedMatchRevision(input["revision"]) ||
    (!legacyV1 && !isMatchMode(input["mode"])) ||
    !isPlayer(input["humanPlayer"])
  ) {
    return invalid("malformed", "Saved match identity or player is malformed.");
  }

  const parsedBot = parseBotPolicy(input["bot"]);
  if (!parsedBot.ok) return parsedBot;

  const parsedController = parseController(input["controller"]);
  if (!parsedController.ok) return parsedController;
  const parsedRecording = legacyV1 || legacyV2 ? null : parseMatchRecording(input["recording"]);
  if (!legacyV1 && !legacyV2 && input["recording"] !== null && parsedRecording === null) {
    return invalid("malformed", "Saved match recording is malformed.");
  }
  const parsedGame = parseGameForRecording(input["game"], parsedRecording);
  if (!parsedGame.ok) {
    const details = [parsedGame.error.message, ...(parsedGame.error.details ?? [])];
    return invalid("invalid-game", "Saved match contains an invalid game.", details);
  }

  if (!controllerMatchesGame(parsedController.controller, parsedGame.state.outcome)) {
    return invalid(
      "inconsistent-controller",
      "Saved match controller result does not match the game outcome.",
    );
  }

  const mode: MatchMode = legacyV1 ? "bot" : (input["mode"] as MatchMode);
  const recording =
    parsedRecording ??
    recoverActiveRecording(input["epoch"], parsedGame.state, parsedController.controller);
  if (recording !== null) {
    if (!recordingMatchesGame(recording, parsedGame.state)) {
      return invalid("inconsistent-controller", "Saved match recording does not match the game.");
    }
    if ((parsedController.controller.route === "finished") !== (recording.completedAt !== null)) {
      return invalid("inconsistent-controller", "Saved match completion metadata is inconsistent.");
    }
    const repeated = recordingEndsInThreefoldRepetition(recording);
    const declaresRepetition =
      parsedController.controller.route === "finished" &&
      parsedController.controller.result.kind === "repetition-draw";
    if (usesRepetitionRules(recording.rulesVersion) && repeated !== declaresRepetition) {
      return invalid("inconsistent-controller", "Saved match repetition result is inconsistent.");
    }
  }
  return Object.freeze({
    ok: true,
    match: Object.freeze({
      schemaVersion: SAVED_MATCH_SCHEMA_VERSION,
      epoch: input["epoch"],
      revision: input["revision"],
      game: serializeGameForRecording(parsedGame.state, recording),
      mode,
      humanPlayer: input["humanPlayer"],
      bot: parsedBot.bot,
      controller: parsedController.controller,
      recording,
    }),
  });
}

function parseGameForRecording(input: unknown, recording: MatchRecordingV1 | null) {
  if (recording?.rulesVersion === LEGACY_MATCH_RULES_VERSION) return parseLegacyGame(input);
  if (recording?.rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) {
    return parseVersion2Game(input);
  }
  if (recording?.rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) {
    return parseVersion3Game(input);
  }
  return parseGame(input);
}

function serializeGameForRecording(game: GameState, recording: MatchRecordingV1 | null) {
  if (recording?.rulesVersion === LEGACY_MATCH_RULES_VERSION) return serializeLegacyGame(game);
  if (recording?.rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) {
    return serializeVersion2Game(game);
  }
  if (recording?.rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) {
    return serializeVersion3Game(game);
  }
  return serializeGame(game);
}

function recoverActiveRecording(
  id: string,
  game: GameState,
  controller: SavedMatchController,
): MatchRecordingV1 | null {
  if (controller.route !== "active") return null;
  return createMatchRecording(id, game);
}

function parseBotPolicy(
  input: unknown,
):
  | Readonly<{ ok: true; bot: SavedBotPolicyV1 }>
  | Readonly<{ ok: false; error: SavedMatchParseError }> {
  if (
    !isRecord(input) ||
    !hasExactlyKeys(input, ["difficulty", "difficultyPolicyVersion", "seedStrategy"]) ||
    !isBotDifficulty(input["difficulty"])
  ) {
    return invalid("malformed", "Saved match bot policy is malformed.");
  }
  if (
    input["difficultyPolicyVersion"] !== BOT_DIFFICULTY_POLICY_VERSION ||
    input["seedStrategy"] !== BOT_SEED_STRATEGY
  ) {
    return invalid("unsupported-bot-policy", "Saved match bot policy is not supported.");
  }
  return Object.freeze({
    ok: true,
    bot: Object.freeze({
      difficulty: input["difficulty"],
      difficultyPolicyVersion: BOT_DIFFICULTY_POLICY_VERSION,
      seedStrategy: BOT_SEED_STRATEGY,
    }),
  });
}

function parseController(
  input: unknown,
):
  | Readonly<{ ok: true; controller: SavedMatchController }>
  | Readonly<{ ok: false; error: SavedMatchParseError }> {
  if (!isRecord(input) || !hasExactlyKeys(input, ["route", "result"])) {
    return invalid("malformed", "Saved match controller data is malformed.");
  }
  if (input["route"] === "active" && input["result"] === null) {
    return Object.freeze({
      ok: true,
      controller: Object.freeze({ route: "active", result: null }),
    });
  }
  if (input["route"] !== "finished") {
    return invalid("malformed", "Saved match controller route is malformed.");
  }

  const result = parseMatchResult(input["result"]);
  if (result === null) {
    return invalid("malformed", "Saved match controller result is malformed.");
  }
  return Object.freeze({
    ok: true,
    controller: Object.freeze({ route: "finished", result }),
  });
}

function parseMatchResult(input: unknown): MatchResult | null {
  if (!isRecord(input)) return null;
  if (input["kind"] === "manual-draw" && hasExactlyKeys(input, ["kind"])) {
    return Object.freeze({ kind: "manual-draw" });
  }
  if (input["kind"] === "repetition-draw" && hasExactlyKeys(input, ["kind"])) {
    return Object.freeze({ kind: "repetition-draw" });
  }
  if (
    input["kind"] === "passage" &&
    hasExactlyKeys(input, ["kind", "winner"]) &&
    isPlayer(input["winner"])
  ) {
    return Object.freeze({ kind: "passage", winner: input["winner"] });
  }
  if (
    input["kind"] === "no-moves" &&
    hasExactlyKeys(input, ["kind", "winner", "loser"]) &&
    isPlayer(input["winner"]) &&
    isPlayer(input["loser"])
  ) {
    return Object.freeze({
      kind: "no-moves",
      winner: input["winner"],
      loser: input["loser"],
    });
  }
  return null;
}

function controllerMatchesGame(controller: SavedMatchController, outcome: GameOutcome | null) {
  if (controller.route === "active") return outcome === null;
  if (controller.result.kind === "manual-draw" || controller.result.kind === "repetition-draw") {
    return outcome === null;
  }
  if (controller.result.kind !== outcome?.kind) return false;
  if (outcome.kind === "passage") {
    return controller.result.kind === "passage" && controller.result.winner === outcome.winner;
  }
  return (
    controller.result.kind === "no-moves" &&
    controller.result.winner === outcome.winner &&
    controller.result.loser === outcome.loser
  );
}

function freezeMatchResult(result: MatchResult): MatchResult {
  return Object.freeze({ ...result });
}

function isMatchEpoch(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_MATCH_EPOCH_LENGTH &&
    /^[A-Za-z0-9._:-]+$/u.test(value)
  );
}

function isMatchVersionRevision(value: unknown): value is number {
  return (
    Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= MAX_MATCH_VERSION_REVISION
  );
}

function isSavedMatchRevision(value: unknown): value is number {
  return isMatchVersionRevision(value) && value <= MAX_SAVED_MATCH_REVISION;
}

function invalid(
  code: SavedMatchParseErrorCode,
  message: string,
  details?: readonly string[],
): Readonly<{ ok: false; error: SavedMatchParseError }> {
  return Object.freeze({
    ok: false,
    error: Object.freeze(details === undefined ? { code, message } : { code, message, details }),
  });
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
